import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { FileText, Calendar } from "lucide-react";

import { useDelayedFlag, useTran } from "@/hooks";
import { SearchableSelect } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/utils";

import { useEsgDashboardStore } from "../../hooks";

const ReportSelector = observer(() => {
  const { t } = useTran();
  const {
    filteredReports,
    selectedReportId,
    selectedReport,
    consolidatedMode,
    consolidatedFramework,
    availableFrameworks,
    loading,
    ensureData,
    setSelectedReportId,
    setConsolidatedMode,
    setConsolidatedFramework,
  } = useEsgDashboardStore();

  const showSkeleton = useDelayedFlag(loading);

  useEffect(() => {
    ensureData();
  }, [ensureData]);

  useEffect(() => {
    if (!consolidatedMode) return;
    if (!consolidatedFramework && availableFrameworks.length) {
      setConsolidatedFramework(availableFrameworks[0]);
    }
  }, [
    availableFrameworks,
    consolidatedFramework,
    consolidatedMode,
    setConsolidatedFramework,
  ]);

  const reportOptions = filteredReports.map((report) => ({
    value: report.id,
    label: `${report.name} - ${report.framework} (${report.reportYear})`,
  }));

  return (
    <div className="rounded-radius border border-border bg-card p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <label className="text-sm font-medium text-foreground">
            {consolidatedMode
              ? t("Consolidated by Framework")
              : t("Select Report to Analyze")}
          </label>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{t("Consolidated")}</span>
          <Switch
            checked={consolidatedMode}
            onCheckedChange={setConsolidatedMode}
          />
        </div>
      </div>

      {showSkeleton ? (
        <Skeleton className="h-9 w-full" />
      ) : consolidatedMode ? (
        <SearchableSelect
          value={consolidatedFramework}
          onChange={setConsolidatedFramework}
          options={availableFrameworks.map((framework) => ({
            value: framework,
            label: framework,
          }))}
          placeholder={t("Select framework to consolidate")}
          searchPlaceholder={t("Search framework")}
          emptyText={t("No frameworks available")}
        />
      ) : (
        <SearchableSelect
          value={selectedReportId}
          onChange={setSelectedReportId}
          options={reportOptions}
          placeholder={t("Choose a report to view performance metrics")}
          searchPlaceholder={t("Search reports")}
          emptyText={t("No reports found")}
        />
      )}

      {selectedReport && !consolidatedMode ? (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {selectedReport.reportingPeriod?.start &&
          selectedReport.reportingPeriod?.end ? (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>
                {t("Period")}:{" "}
                {formatDateTime(selectedReport.reportingPeriod.start, {
                  dateStyle: "medium",
                })}{" "}
                -{" "}
                {formatDateTime(selectedReport.reportingPeriod.end, {
                  dateStyle: "medium",
                })}
              </span>
            </div>
          ) : null}
          {selectedReport.status ? (
            <Badge variant="outline" className="text-xs capitalize">
              {selectedReport.status.replace("_", " ")}
            </Badge>
          ) : null}
        </div>
      ) : null}

      {!showSkeleton && filteredReports.length === 0 ? (
        <div className="mt-4 rounded-radius border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
          {t("No ESG reports for this organization.")}
        </div>
      ) : null}
    </div>
  );
});

export default ReportSelector;
